// Request types and mapping helpers for the fumigation application submission API.
// Mirrors the server contract defined in
// server/src/functions/submit-fumigation-application/handlers/post.handler.ts

import { applicationData, FumigationRecord } from "contexts/applicationDataFieldTypes";

// ── Wire format ───────────────────────────────────────────────────────────────

/** A single customer / premise row as expected by the API. */
export interface FumigationSubmissionRecord {
    customerName: string;
    premiseAddress: string;
    /** Combined close date and time, e.g. "07/20/2026 AM". */
    closeSchedule: string;
    /** Combined restore date and time, e.g. "07/22/2026 AD". */
    restoreSchedule: string;
    qty: number;
    aer: string;
    accessArrangements: string;
    codeType: string;
    code: string;
}

/** Full POST body for the fumigation application submission. */
export interface FumigationSubmissionPayload {
    fumigationCompanyName: string;
    contactPerson: string;
    /** Formatted for display, e.g. "(619) 555-1234 x102". */
    telephoneNumber: string;
    emailAddress: string;
    alternateContact: string;
    alternateTelephoneNumber: string;
    alternateEmailAddress: string;
    additionalAccessInformation: string;
    records: FumigationSubmissionRecord[];
}

export interface FumigationSubmissionResponse {
    data?: string;
    message?: string;
    messageId?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const trim = (value?: string | null): string => (value ?? '').trim();

/**
 * Formats split phone parts into a single display string.
 * Returns an empty string when no digits were supplied at all.
 */
export const formatPhoneNumber = (
    areaCode?: string,
    mid3?: string,
    last4?: string,
    extension?: string,
): string => {
    const area = trim(areaCode);
    const mid = trim(mid3);
    const last = trim(last4);
    const ext = trim(extension);

    if (!area && !mid && !last) {
        return ext ? `x${ext}` : '';
    }

    const base = `(${area}) ${mid}-${last}`;
    return ext ? `${base} x${ext}` : base;
};

/**
 * Combines a schedule date ("MM/dd/yyyy") with its time slot ("AM" | "PM" | "AD").
 * Returns an empty string when no date was selected.
 */
export const combineSchedule = (date?: string, time?: string): string => {
    const d = trim(date);
    if (!d) return '';
    const t = trim(time);
    return t ? `${d} ${t}` : d;
};

/**
 * Matches the legacy validation rule:
 * `if (fumLnNNAddress.Text.Trim() == "") swValLnNN = "E";`
 * A row is only considered filled — and therefore submitted — when it has a premise address.
 */
export const isFilledRecord = (record: FumigationRecord): boolean =>
    Boolean(trim(record.premiseAddress));

const toSubmissionRecord = (record: FumigationRecord): FumigationSubmissionRecord => ({
    customerName: trim(record.customerName),
    premiseAddress: trim(record.premiseAddress),
    closeSchedule: combineSchedule(record.closeSchedule, record.closeTime),
    restoreSchedule: combineSchedule(record.restoreSchedule, record.restoreTime),
    qty: Number(record.qty) || 0,
    aer: trim(record.aer),
    accessArrangements: trim(record.accessArrangements),
    codeType: trim(record.codeType),
    code: trim(record.code),
});

// ── Mapper ────────────────────────────────────────────────────────────────────

/**
 * Maps the in-app form state onto the API request body. Drops the client-only
 * fields (row ids, split phone parts) and skips empty placeholder rows.
 */
export const buildFumigationSubmissionPayload = (
    data: applicationData,
): FumigationSubmissionPayload => ({
    fumigationCompanyName: trim(data.fumigationCompanyName),
    contactPerson: trim(data.contactPerson),
    telephoneNumber: formatPhoneNumber(
        data.telephoneAreaCode,
        data.telephoneMid3,
        data.telephoneLast4,
        data.telephoneExtension,
    ),
    emailAddress: trim(data.emailAddress),
    alternateContact: trim(data.alternateContact),
    alternateTelephoneNumber: formatPhoneNumber(
        data.alternateAreaCode,
        data.alternateMid3,
        data.alternateLast4,
        data.alternateExtension,
    ),
    alternateEmailAddress: trim(data.alternateEmailAddress),
    additionalAccessInformation: trim(data.additionalAccessInformation),
    records: (data.records ?? []).filter(isFilledRecord).map(toSubmissionRecord),
});
